/**
 * Determines whether the RefreshAnalysis button should be enabled
 * @param {Object} currentBook - The current book object with content versions
 * @param {Object} analysisReport - The analysis report object from useBookPublishabilityReport
 * @returns {boolean} - True if refresh should be enabled
 */
export const shouldEnableRefreshAnalysis = (currentBook, analysisReport) => {
  // Always enable if no analysis report exists
  console.log('shouldEnableRefreshAnalysis');

  if (!analysisReport || !analysisReport.results) {
    console.log('No analysis report or results - enabling refresh');

    return true;
  }

  // Get the analysis results
  const results = analysisReport.results;
  const status = results.status || analysisReport.status;

  // Always enable if analysis is in error state
  if (status === 'error') {
    console.log('Analysis in error state - enabling refresh');

    return true;
  }

  // Disable if analysis is currently in progress
  const processingStatuses = [
    'pending',
    'uploaded',
    'extracting_metadata',
    'analyzing_content',
    'analyzing_publishability',
  ];

  if (processingStatuses.includes(status)) {
    console.log('Analysis in progress - disabling refresh');

    return false;
  }

  // Get the latest content version
  const content = currentBook?.content || [];
  if (content.length === 0) {
    console.log('No content versions - enabling refresh');

    return true; // Enable if no content versions exist
  }

  const latestContent = content[content.length - 1];
  const latestContentId = latestContent?._id || latestContent?.id;

  // Strategy 1: Check if we're analyzing a different content version
  const analyzedContentId = results.analyzedContentId;
  if (analyzedContentId && latestContentId !== analyzedContentId) {
    console.log('Different content version - enabling refresh');

    return true; // Different version - enable refresh
  }

  // Strategy 2: Check if content was modified after analysis completed
  // Check for Mongoose timestamps (createdAt/updatedAt) at root level or nested updatedAt
  const contentUpdatedAt = latestContent?.updatedAt || latestContent?.createdAt;
  const analysisStartedAt = results.startedAt || analysisReport.startedAt || results.updatedAt || analysisReport.updatedAt;

  if (contentUpdatedAt && analysisStartedAt) {
    const contentDate = new Date(contentUpdatedAt);
    const analysisDate = new Date(analysisStartedAt);

    // Enable if content is newer than analysis
    console.log('refresh contentversion analysis debug:', {
      latestContent,
      latestContentId,
      analyzedContentId,
      contentUpdatedAt,
      analysisStartedAt,
      status,
    });
    if (contentDate > analysisDate) {
      console.log('Content updated after analysis - enabling refresh');

      return true;
    }
  }

  // Fallback: If we can't determine timestamps reliably, be conservative and allow refresh
  // unless we're certain the analysis is for the exact same content version

  if (!contentUpdatedAt || !analysisStartedAt) {
    console.log('Missing timestamps - enabling refresh for safety', {
      contentUpdatedAt,
      analysisUpdatedAt: analysisStartedAt,
    });

    return true;
  }

  // Only disable if we have the same content version AND analysis is newer than content
  // This is conservative - when in doubt, allow refresh
  console.log(
    'All checks passed - analysis appears up to date, disabling refresh'
  );
  return false;
};
